import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { MdCheck, MdExpandMore, MdImage, MdOpenInNew, MdSearch } from "react-icons/md";
import type { AdRow, LeadRow, OrderBrief } from "../api/types";
import { adHook, adLabel, dateInputDay, dayInput, RANGE_KEYS, todayNo, type DateRange } from "../customers";
import { orderStage, orderWord } from "../orders";
import { PROGRESS, progressLabel } from "../progress";
import { avatarStyle, initialOf, ORIGINS, originLabel, phoneLabel } from "../ui";
import { AdJourneySheet } from "./AdJourney";
import AvatarPic from "./AvatarPic";
import { imeComposing } from "../util";

/** The pieces the customer table's three views share. */

export function Avatar({ row, size = 36 }: { row: Pick<LeadRow, "wa_id" | "name" | "avatar_url">; size?: number }) {
  return (
    <span className="zn-av" style={{ ...avatarStyle(row.wa_id), width: size, height: size, fontSize: size * 0.42 }} aria-hidden="true">
      <AvatarPic url={row.avatar_url} />
      {initialOf(row.name || row.wa_id)}
    </span>
  );
}

/** The ad's picture, or a quiet placeholder while Meta has not given one. */
export function AdPic({ src, size = 32 }: { src?: string; size?: number }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="zn-adpic" style={{ width: size, height: size }}>
      {src && !broken ? (
        <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} />
      ) : (
        <MdImage />
      )}
    </span>
  );
}

/**
 * An ad as the team recognises it: its picture and a short name, the full
 * campaign on hover; a link that opens the ad when Meta gave one.
 */
export function AdBadge({
  campaign,
  headline,
  thumb,
  link,
  noted,
  size = 32,
}: {
  campaign?: string;
  headline?: string;
  thumb?: string;
  link?: string;
  /** CS wrote the campaign down; Meta sent no ad. */
  noted?: boolean;
  size?: number;
}) {
  const full = (campaign || headline || "广告") + (noted ? "\n客服记的广告系列" : "");
  const body = (
    <>
      <AdPic src={thumb} size={size} />
      <span className="zn-adname">{campaign ? adLabel(campaign) : full}</span>
    </>
  );
  if (!link) {
    return (
      <span className="zn-ad" title={full}>
        {body}
      </span>
    );
  }
  return (
    <a className="zn-ad link" href={link} target="_blank" rel="noreferrer" title={`${full}\n点一下看这条广告`} onClick={(e) => e.stopPropagation()}>
      {body}
      <MdOpenInNew className="go" />
    </a>
  );
}

/** An order's state as an outlined pill, the same everywhere. */
export function OrderPill({ o, word }: { o: Pick<OrderBrief, "payment_status" | "fulfillment_status" | "payment_method">; word?: string }) {
  return <span className={`zn-opill o-${orderStage(o)}`}>{word ?? orderWord(o)}</span>;
}

/** The progress word behind its dot, read-only. */
export function ProgressWord({ row }: { row: Pick<LeadRow, "status" | "status_by"> }) {
  const value = row.status || "new";
  return (
    <span className="zn-prog">
      <i className={`zn-dot p-${value}`} />
      {progressLabel(value)}
      {row.status_by === "auto" && <small title="按聊天和订单自动改的">自动</small>}
    </span>
  );
}

/** A panel under a button that closes on a click elsewhere or Esc. */
export function Pop({
  label,
  active,
  children,
  align = "left",
  width,
}: {
  label: ReactNode;
  active?: boolean;
  children: (close: () => void) => ReactNode;
  align?: "left" | "right";
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  // Moved in, before it is painted, when it would end past the window's edge:
  // on a phone 全部广告's panel, held to its button's right, started 148px off
  // the left. The panel is new each time it opens, so it starts unmoved.
  useLayoutEffect(() => {
    const p = panel.current;
    if (!open || !p) return;
    const r = p.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const dx = Math.max(MENU_EDGE - r.left, Math.min(0, vw - MENU_EDGE - r.right));
    if (dx) p.style.transform = `translateX(${dx}px)`;
  }, [open]);
  return (
    <div className="zn-pop" ref={box}>
      <button type="button" className={`zn-popbtn${active ? " on" : ""}${open ? " open" : ""}`} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {label}
        <MdExpandMore />
      </button>
      {open && (
        <div ref={panel} className={`zn-poppanel${align === "right" ? " right" : ""}`} style={width ? { width } : undefined}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

/** 今天 · 昨天 · 7 天 · 30 天 · 全部 · 自定, the report's periods; 自定 opens two dates.
 *  keys: a page that cannot read 全部 leaves it out. what="" drops the label. */
export function DateFilter({
  value,
  onChange,
  what = "来的日期",
  keys = RANGE_KEYS,
}: {
  value: DateRange;
  onChange: (r: DateRange) => void;
  what?: string;
  keys?: typeof RANGE_KEYS;
}) {
  const t = todayNo();
  return (
    <div className="zn-datef">
      {what && <span className="zn-fl">{what}</span>}
      <div className="zn-seg sm" role="tablist">
        {keys.map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={value.key === k}
            className={value.key === k ? "on" : ""}
            onClick={() =>
              onChange(
                k === "custom"
                  ? { key: "custom", from: value.from || dayInput(t - 6), to: value.to || dayInput(t) }
                  : { key: k, from: value.from, to: value.to },
              )
            }
          >
            {label}
          </button>
        ))}
      </div>
      {value.key === "custom" && (
        <span className="zn-dates2">
          <input
            type="date"
            value={value.from}
            max={value.to || undefined}
            onChange={(e) => onChange({ ...value, from: e.target.value })}
            aria-label="从"
          />
          <span>到</span>
          <input
            type="date"
            value={value.to}
            min={value.from || undefined}
            onChange={(e) => onChange({ ...value, to: e.target.value })}
            aria-label="到"
          />
          {!Number.isNaN(dateInputDay(value.from)) && !Number.isNaN(dateInputDay(value.to)) && (
            <em>{Math.max(0, dateInputDay(value.to) - dateInputDay(value.from) + 1)} 天</em>
          )}
        </span>
      )}
    </div>
  );
}

/** Pick one progress word, each with its count; every word on screen at once. */
export function ProgressFilter({ value, counts, onChange }: { value: string; counts: Record<string, number>; onChange: (v: string) => void }) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return (
    <Pop
      active={value !== ""}
      label={
        value ? (
          <>
            <i className={`zn-dot p-${value}`} /> {progressLabel(value)}
          </>
        ) : (
          "全部进度"
        )
      }
      width={300}
    >
      {(close) => (
        <div className="zn-optgrid">
          <button
            type="button"
            className={`zn-opt wide${value === "" ? " on" : ""}`}
            onClick={() => {
              onChange("");
              close();
            }}
          >
            全部进度 <b>{total}</b>
          </button>
          {PROGRESS.map((p) => (
            <button
              key={p.value}
              type="button"
              className={`zn-opt${value === p.value ? " on" : ""}${counts[p.value] ? "" : " none"}`}
              onClick={() => {
                onChange(p.value);
                close();
              }}
            >
              <i className={`zn-dot p-${p.value}`} />
              {p.label}
              <b>{counts[p.value] ?? 0}</b>
            </button>
          ))}
        </div>
      )}
    </Pop>
  );
}

/** Pick one ad campaign: a search box over one line per campaign, most customers first. */
export function AdFilter({
  value,
  options,
  none,
  onChange,
}: {
  value: string;
  options: Array<{ campaign: string; n: number; thumb?: string }>;
  none: number;
  onChange: (v: string) => void;
}) {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter((o) => o.campaign.toLowerCase().includes(needle)) : options;
  }, [options, q]);
  const pick = (v: string, close: () => void) => {
    onChange(v);
    setQ("");
    close();
  };
  const label = value === "" ? "全部广告" : value === "-" ? "不是广告来的" : adLabel(value);
  return (
    <Pop active={value !== ""} label={<span className="zn-popval">{label}</span>} width={380} align="right">
      {(close) => (
        <div className="zn-adfilter">
          <label className="zn-adsearch">
            <MdSearch />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜广告名字" />
          </label>
          <div className="zn-adlist">
            {!q && (
              <button type="button" className={`zn-adopt${value === "" ? " on" : ""}`} onClick={() => pick("", close)}>
                <span>全部广告</span>
                {value === "" && <MdCheck />}
              </button>
            )}
            {shown.map((o) => (
              <button
                key={o.campaign}
                type="button"
                className={`zn-adopt pic${value === o.campaign ? " on" : ""}`}
                onClick={() => pick(o.campaign, close)}
                title={o.campaign}
              >
                <AdPic src={o.thumb} size={36} />
                <span>
                  <span>{adLabel(o.campaign)}</span>
                  {adHook(o.campaign) && <small>{adHook(o.campaign)}</small>}
                </span>
                <b>{o.n} 人</b>
              </button>
            ))}
            {!q && (
              <button type="button" className={`zn-adopt muted${value === "-" ? " on" : ""}`} onClick={() => pick("-", close)}>
                <span>不是广告来的</span>
                <b>{none} 人</b>
              </button>
            )}
            {q && shown.length === 0 && <div className="zn-adnone">没有叫这个名字的广告</div>}
          </div>
        </div>
      )}
    </Pop>
  );
}

/** A campaign to pick for a customer, with the pictures and headlines of
 *  its ads: two campaigns can share a picture (排毒014 and 排毒 mofu - Copy
 *  did), and then the headline under the phone's ad card tells them apart. */
export interface CampaignChoice {
  campaign: string;
  thumbs: string[];
  heads: string[];
}

/**
 * A customer who came from an ad that WhatsApp never named: their first line
 * reached the phone alone (error 131060 — 「This message is currently
 * unavailable」), the ad with it, and the API got neither. The server counts
 * them as ad leads with the ad unknown; a colleague picks it from what the
 * phone shows. Owner, 2026-10-04: 「怎么避免这样的事再次发生？」.
 */
export const adMissing = (r: LeadRow) => r.origin === "ad" && !r.ad_id && !r.ad_campaign && !r.ad_guess;

/**
 * 广告没收到 that the server has filled in: the ad Meta counted more
 * conversations for than were kept that day, counted in the report while the
 * method is trusted. Nobody has to look on the phone for these; a colleague
 * can still pick another.
 */
export const adFilled = (r: LeadRow) => adMissing(r) && !!r.ad_inferred_applied && !!r.ad_inferred_campaign;

/** 广告没收到 still waiting for a colleague: not filled in by the server. */
export const adToPick = (r: LeadRow) => adMissing(r) && !adFilled(r);

/**
 * Every campaign the account has run, dearest first, each with the pictures of
 * its ads, dearest first: the card at the top of a customer's chat on the phone
 * shows one of them, and that is how CS know which it was.
 */
export function campaignChoices(ads: AdRow[]): CampaignChoice[] {
  const by = new Map<string, { spend: number; thumbs: string[]; heads: string[] }>();
  for (const a of [...ads].sort((x, y) => (y.spend ?? 0) - (x.spend ?? 0))) {
    if (!a.ad_id || !a.campaign || a.noted) continue;
    const g = by.get(a.campaign) ?? { spend: 0, thumbs: [], heads: [] };
    g.spend += a.spend ?? 0;
    if (a.thumb && !g.thumbs.includes(a.thumb)) g.thumbs.push(a.thumb);
    const head = (a.headline ?? "").trim();
    if (head && !g.heads.includes(head)) g.heads.push(head);
    by.set(a.campaign, g);
  }
  return [...by.entries()]
    .sort((x, y) => y[1].spend - x[1].spend)
    .map(([campaign, g]) => ({ campaign, thumbs: g.thumbs.slice(0, 3), heads: g.heads.slice(0, 2) }));
}

/**
 * 点的广告, for a customer Meta sent no ad for — everyone before 2026-09-15:
 * CS scroll back to the ad card at the top of the chat on the phone and pick
 * the campaign with that picture. An ad Meta did send is shown, never picked over.
 */
export function AdPickCell(props: {
  row: LeadRow;
  editable: boolean;
  choices: CampaignChoice[] | null;
  onPick: (campaign: string, thumb?: string) => void;
}) {
  // Tapped more than the one shown: 「+N」 beside it opens their whole 广告足迹 (owner, 2026-10-12:
  // 「如果我想知道顾客点过什么广告我要到哪里看？」).
  const [road, setRoad] = useState(false);
  const more = (props.row.ads_tapped ?? 0) - 1;
  if (more < 1) return <AdPick {...props} />;
  return (
    <span className="zn-adcell">
      <AdPick {...props} />
      <button
        type="button"
        className="zn-admore"
        title={`点过 ${more + 1} 条广告，看广告足迹`}
        onClick={(e) => {
          e.stopPropagation();
          setRoad(true);
        }}
      >
        +{more}
      </button>
      {road && <AdJourneySheet waID={props.row.wa_id} name={props.row.name || phoneLabel(props.row.wa_id)} onClose={() => setRoad(false)} />}
    </span>
  );
}

function AdPick({
  row,
  editable,
  choices,
  onPick,
}: {
  row: LeadRow;
  editable: boolean;
  choices: CampaignChoice[] | null;
  onPick: (campaign: string, thumb?: string) => void;
}) {
  const [q, setQ] = useState("");
  const { open, setOpen, pos, btn, menu, toggle } = usePickMenu();
  // 广告没收到: what the server worked out comes first — its pick, then the
  // campaigns short that day — so the right one is at the top of the list.
  const sys = adMissing(row) ? (row.ad_inferred_campaign ?? "") : "";
  const maybe = adMissing(row) ? (row.ad_candidates ?? []) : [];
  const first = [sys, ...maybe].filter(Boolean);
  const firstKey = first.join("|");
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (choices ?? []).filter(
      (c) => !needle || c.campaign.toLowerCase().includes(needle) || c.heads.some((h) => h.toLowerCase().includes(needle)),
    );
    const top = firstKey ? firstKey.split("|") : [];
    if (needle || top.length === 0) return list;
    const rank = (c: CampaignChoice) => (top.includes(c.campaign) ? top.indexOf(c.campaign) : top.length);
    return [...list].sort((a, b) => rank(a) - rank(b));
  }, [choices, q, firstKey]);

  const badge =
    row.ad_campaign || row.ad_id ? (
      <AdBadge campaign={row.ad_campaign} headline={row.ad_headline} thumb={row.ad_thumb} link={row.ad_link} noted={row.ad_noted} />
    ) : row.ad_guess ? (
      // Before 2026-09-15 only the click was kept: the first line says which
      // ad, as a guess, and a colleague's pick replaces it.
      <span className="zn-adguess" title={`9/15 以前没记下是哪一条广告，看第一句话推断是：${row.ad_guess}`}>
        <span>{row.ad_guess.startsWith("「") ? row.ad_guess : adLabel(row.ad_guess)}</span>
        <small>推断</small>
      </span>
    ) : adFilled(row) ? (
      <span className="zn-adinf" title="WhatsApp 没把这个客户的第一句送来；系统按 Meta 每条广告当天开始的对话数认出是这条">
        <AdBadge campaign={row.ad_inferred_campaign} headline={row.ad_inferred_name} thumb={row.ad_inferred_thumb} />
        <small>自动猜的</small>
      </span>
    ) : adMissing(row) ? (
      <span className="zn-admiss" title="WhatsApp 没把这个客户的第一句送来，不知道是哪个广告">
        广告不明
      </span>
    ) : (
      <span className="dim">—</span>
    );
  if ((row.ad_id && !row.ad_noted) || !editable) return badge;
  return (
    <>
      <button
        ref={btn}
        type="button"
        className="zn-adpick"
        title={
          row.ad_noted
            ? "客服记的广告，点一下改"
            : adFilled(row)
              ? "自动猜的广告；不对的话点一下，选手机上看到的那个"
              : adMissing(row)
                ? "在手机打开这个对话，点最上面的「Facebook ad」看图和标题，在这里选同一个"
                : "看手机上这个客户点进来的广告，点一下记下来"
        }
        onClick={() => {
          setQ("");
          toggle();
        }}
      >
        {row.ad_campaign || adFilled(row) ? (
          badge
        ) : adMissing(row) ? (
          <span className="zn-admiss">广告不明 · 选一个</span>
        ) : (
          <span className="add">＋ 记广告</span>
        )}
      </button>
      {open &&
        createPortal(
          <div ref={menu} className="zn-adpickmenu" style={pos}>
            <label className="zn-adsearch">
              <MdSearch />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜广告名字" />
            </label>
            <div className="list">
              {choices === null && <div className="zn-adnone">读取中…</div>}
              {choices !== null && shown.length === 0 && <div className="zn-adnone">没有叫这个名字的广告</div>}
              {shown.map((c) => (
                <button
                  key={c.campaign}
                  type="button"
                  className={`opt${row.ad_campaign === c.campaign ? " on" : ""}`}
                  title={c.campaign}
                  onClick={() => {
                    setOpen(false);
                    onPick(c.campaign, c.thumbs[0]);
                  }}
                >
                  <span className="pics">
                    {c.thumbs.length === 0 ? <AdPic size={34} /> : c.thumbs.map((t) => <AdPic key={t} src={t} size={34} />)}
                  </span>
                  <span className="nm">
                    <b>
                      {adLabel(c.campaign)}
                      {c.campaign === sys ? <em className="sys">自动猜的</em> : maybe.includes(c.campaign) ? <em className="sys">可能</em> : null}
                    </b>
                    {c.heads.length > 0 ? (
                      <small title={c.heads.join(" / ")}>标题：{c.heads.join(" / ")}</small>
                    ) : (
                      adHook(c.campaign) && <small>{adHook(c.campaign)}</small>
                    )}
                  </span>
                  {row.ad_campaign === c.campaign && <MdCheck />}
                </button>
              ))}
            </div>
            {row.ad_noted && (
              <button
                type="button"
                className="clear"
                onClick={() => {
                  setOpen(false);
                  onPick("");
                }}
              >
                拿掉这个广告
              </button>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

/** How far a pinned list keeps from its button, and from the window's edges. */
const MENU_GAP = 4;
const MENU_EDGE = 8;

/** The progress word as a small menu, for a colleague to change it in place. */
/**
 * A list that opens under a cell and is pinned to the WINDOW, not to the
 * table: inside the scroll box it was cut off at the edge. Shared by 进度,
 * 来源 and 点的广告, which are the same gesture on three columns.
 *
 * It opens downwards when it fits, else towards the side with more room, no
 * taller than that room, and is moved in to end inside the window. Turning
 * upwards whenever the whole list did not fit below put the ad list's search
 * box above the top of the owner's 730px-high window for the upper rows of
 * the table, and on a phone every list ran off the right edge.
 *
 * The list itself is drawn on the page's body (createPortal), not in its cell:
 * each day of 全部客户 is a box that keeps what is inside it
 * (content-visibility), and a fixed list inside one is placed against that box
 * and cut off by it (2026-10-20). styles.css gives it the size and colour it
 * had in the cell.
 */
function usePickMenu() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number; maxHeight?: number }>({ left: 0, top: 0 });
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      const t = e.target as Node;
      if (btn.current?.contains(t) || menu.current?.contains(t)) return;
      setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    // The menu is placed against the window; a scroll would leave it behind.
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  // Placed from the size it is drawn at, before anything is painted. Measured
  // only when open: three of these on every row of 全部客户, each measuring
  // its button as it was drawn, made the browser lay out the whole table in
  // the middle of drawing it.
  useLayoutEffect(() => {
    if (!open) return;
    const r = btn.current?.getBoundingClientRect();
    const m = menu.current;
    if (!r || !m) return;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    // The stylesheet's own cap (the ad list's 450px) still holds where there is room.
    const cap = parseFloat(getComputedStyle(m).maxHeight) || Infinity;
    const below = vh - r.bottom - MENU_GAP - MENU_EDGE;
    const above = r.top - MENU_GAP - MENU_EDGE;
    const left = Math.max(MENU_EDGE, Math.min(r.left, vw - MENU_EDGE - m.offsetWidth));
    setPos(
      m.offsetHeight <= below || below >= above
        ? { left, top: r.bottom + MENU_GAP, maxHeight: Math.min(cap, below) }
        : { left, bottom: vh - r.top + MENU_GAP, maxHeight: Math.min(cap, above) },
    );
  }, [open]);

  const toggle = () => {
    // Drawn at the top left first: there the ad list's autofocus has nothing
    // to scroll into view, and a scroll would close it.
    setPos({ left: 0, top: 0 });
    setOpen((o) => !o);
  };
  return { open, setOpen, pos, btn, menu, toggle };
}

/**
 * 来源: where this customer came from. 广告/网店/Messenger come from the click;
 * 代理/转介绍/回头客 only a colleague knows, and until one says so the cell
 * shows our guess marked 自动 — a guess must never read as an answer.
 */
export function OriginCell({ row, editable, onPick }: { row: Pick<LeadRow, "origin" | "origin_chosen">; editable: boolean; onPick: (v: string) => void }) {
  const value = row.origin ?? "";
  const { open, setOpen, pos, btn, menu, toggle } = usePickMenu();
  const word = (
    <>
      {originLabel(value)}
      {value !== "" && !row.origin_chosen && <small title="按广告点击、第一句话自动判断的，你选了就以你为准">自动</small>}
    </>
  );
  if (!editable) return <span className={`zn-origin${value ? "" : " none"}`}>{word}</span>;
  return (
    <>
      <button ref={btn} type="button" className={`zn-origin pick${value ? "" : " none"}`} aria-haspopup="listbox" aria-expanded={open} onClick={toggle}>
        {word}
      </button>
      {open &&
        createPortal(
          <div ref={menu} className="zn-progmenu" role="listbox" style={pos}>
            {ORIGINS.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={o.value === value ? "on" : ""}
                onClick={() => {
                  setOpen(false);
                  onPick(o.value === value ? "" : o.value);
                }}
              >
                {o.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

export function ProgressCell({ row, editable, onPick }: { row: LeadRow; editable: boolean; onPick: (v: string) => void }) {
  const value = row.status || "new";
  const { open, setOpen, pos, btn, menu, toggle } = usePickMenu();

  if (!editable) return <ProgressWord row={row} />;
  return (
    <>
      <button ref={btn} type="button" className="zn-prog pick" aria-haspopup="listbox" aria-expanded={open} onClick={toggle}>
        <i className={`zn-dot p-${value}`} />
        {progressLabel(value)}
        {row.status_by === "auto" && <small title="按聊天和订单自动改的，改了就以你为准">自动</small>}
      </button>
      {open &&
        createPortal(
          <div ref={menu} className="zn-progmenu" role="listbox" style={pos}>
            {PROGRESS.map((p) => (
              <button
                key={p.value}
                type="button"
                role="option"
                aria-selected={p.value === value}
                className={p.value === value ? "on" : ""}
                onClick={() => {
                  setOpen(false);
                  if (p.value !== value) onPick(p.value);
                }}
              >
                <i className={`zn-dot p-${p.value}`} />
                {p.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

/** The note, typed straight into the cell; saved on Enter or leaving it. */
export function NoteCell({ row, editable, onSave }: { row: LeadRow; editable: boolean; onSave: (v: string) => Promise<void> }) {
  const server = row.note ?? "";
  const [draft, setDraft] = useState(server);
  const [state, setState] = useState<"" | "saving" | "saved" | "failed">("");
  const focused = useRef(false);

  // A refresh brings the server's note; it must not overwrite what is being typed.
  useEffect(() => {
    if (!focused.current) setDraft(server);
  }, [server]);

  async function save() {
    if (draft === server) return;
    setState("saving");
    try {
      await onSave(draft);
      setState("saved");
      window.setTimeout(() => setState((s) => (s === "saved" ? "" : s)), 1500);
    } catch {
      setState("failed");
    }
  }

  // Nobody has written one: what the system knows stands in, grey, until a colleague types their own
  // (owner, 2026-10-11: 「备注是拿来干嘛的？怎么都没用？」).
  const auto = row.auto_note ?? "";
  if (!editable) return server ? <span className="zn-note ro">{server}</span> : <span className="zn-note ro auto">{auto}</span>;
  return (
    <input
      className={`zn-note${state ? " " + state : ""}${!draft && auto ? " auto" : ""}`}
      value={draft}
      placeholder={auto || "＋ 备注"}
      title={state === "failed" ? "没存上，再按一次 Enter" : draft || (auto ? `${auto}（小爱记的，可以直接改成同事的备注）` : "")}
      onFocus={() => (focused.current = true)}
      onChange={(e) => {
        setDraft(e.target.value);
        if (state === "failed") setState("");
      }}
      onBlur={() => {
        focused.current = false;
        void save();
      }}
      onKeyDown={(e) => {
        if (imeComposing(e)) return;
        if (e.key === "Enter") {
          e.preventDefault();
          void save();
        } else if (e.key === "Escape") {
          setDraft(server);
          setState("");
        }
      }}
    />
  );
}
